import React, { useState, useEffect } from 'react';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { isValidPhoneNumber as validatePhone } from 'libphonenumber-js';

interface InternationalPhoneInputProps {
  value?: string;
  onChange: (phone: string, isValid: boolean) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  label?: string;
  required?: boolean;
  error?: string;
  defaultCountry?: string;
}

export const InternationalPhoneInput: React.FC<InternationalPhoneInputProps> = ({
  value = '',
  onChange,
  placeholder = 'Enter phone number',
  disabled = false,
  className = '',
  label,
  required = false,
  error,
  defaultCountry = 'in' // Default to India
}) => {
  const [phone, setPhone] = useState(value);
  const [isValid, setIsValid] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);

  useEffect(() => {
    setPhone(value);
    // Validate existing value when it changes
    if (value && value.length > 0) {
      try {
        setIsValid(validatePhone(value));
      } catch (_error) {
        setIsValid(false);
      }
    } else {
      setIsValid(false);
    }
  }, [value]);

  const handlePhoneChange = (phone: string, country: any, metadata?: any) => {
    setPhone(phone);
    // Use the library's built-in validation function
    // PhoneInput provides phone in E.164 format (e.g., +19293448376)
    let valid = false;
    
    // Only validate if we have a phone number and country info
    if (phone && phone.length > 0 && country) {
      try {
        // PhoneInput component formats phone correctly in E.164 format
        // Check if phone starts with country code
        if (phone.startsWith('+') && country.dialCode) {
          // Remove any spaces, parentheses, or hyphens from phone for validation
          const cleanPhone = phone.replace(/\s/g, '').replace(/[()-]/g, '');
          
          // Check minimum length - country code + at least some digits
          // Be more lenient: only require country code + 3 digits minimum while typing
          const minLength = country.dialCode.length + 3;
          if (cleanPhone.length >= minLength) {
            // Only validate if phone seems complete (has reasonable length)
            // For most countries, a complete number is country code + 7-15 digits
            const maxLength = country.dialCode.length + 15;
            if (cleanPhone.length <= maxLength) {
              valid = validatePhone(cleanPhone);
            }
            // If still typing and length is reasonable, don't mark as invalid yet
            // Only mark as invalid if we've validated and it failed
            if (!valid && cleanPhone.length < country.dialCode.length + 7) {
              // User is still typing, don't show error yet
              valid = true; // Don't show error while typing
            }
          } else {
            // Too short, but user is still typing
            valid = true; // Don't show error while typing
          }
        }
      } catch (_error) {
        // If validation fails, but user might still be typing
        // Only mark as invalid if we've blurred (user finished typing)
        if (hasBlurred) {
          valid = false;
        } else {
          valid = true; // Don't show error while typing
        }
      }
    }
    
    setIsValid(valid);
    onChange(phone, valid);
  };

  const handleBlur = () => {
    setHasBlurred(true);
    // Re-validate on blur with stricter rules
    if (phone && phone.length > 0) {
      try {
        const cleanPhone = phone.replace(/\s/g, '').replace(/[()-]/g, '');
        if (cleanPhone.startsWith('+')) {
          const valid = validatePhone(cleanPhone);
          setIsValid(valid);
          onChange(phone, valid);
        } else {
          // If no country code, try to validate with default country
          setIsValid(false);
          onChange(phone, false);
        }
      } catch (_error) {
        // If validation throws an error, try without strict validation
        // Sometimes libphonenumber-js is too strict, so we'll be more lenient
        const cleanPhone = phone.replace(/\s/g, '').replace(/[()-]/g, '');
        if (cleanPhone.startsWith('+') && cleanPhone.length >= 10) {
          try {
            const valid = validatePhone(cleanPhone);
            setIsValid(valid);
            onChange(phone, valid);
          } catch (_e) {
            setIsValid(false);
            onChange(phone, false);
          }
        } else {
          setIsValid(false);
          onChange(phone, false);
        }
      }
    }
  };

  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className={`relative ${error ? 'ring-2 ring-red-500' : ''}`}>
        <PhoneInput
          value={phone}
          onChange={handlePhoneChange}
          onBlur={handleBlur}
          defaultCountry={defaultCountry}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full"
          inputClassName="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed bg-white text-gray-900"
          countrySelectorStyleProps={{
            className: "border-r border-gray-300 bg-gray-50 rounded-l-lg"
          }}
          // Disable default validation error display - we handle it ourselves
          disableValidation={false}
        />
      </div>
      
      {error && (
        <p className="text-sm text-red-600 mt-1">{error}</p>
      )}
      
      {/* Show validation feedback similar to email field */}
      {phone && phone.replace(/\s/g, '').length > 0 && (
        <>
          {isValid && (
            <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Phone is valid
            </p>
          )}
          {!isValid && (hasBlurred || phone.replace(/\s/g, '').length >= 10) && (
            <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Please enter a valid phone number
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default InternationalPhoneInput;
